import * as angular from "angular";
import {IController} from "angular";
import * as _ from "lodash";
import {SplitService} from "../../../commons/services/split-service.service";
import {FulfillmentPackageSplitType} from "../../types/fulfillment-package-split.type";
import {FulfillmentStepStatusType} from "../../types/fulfillment-step-status.type";
import {PackageDetailsViewSettingsService} from "../../../commons/services/package-details-view-settings.service";


export class FulfillmentLinePackagesTableComponent implements IController {

  packagesList: any;
  fulfillmentLineSteps: any[];
  weightUnit: any;
  volumeUnit: any;
  distanceUnit: any;
  packageDynamicColumns: any;
  isShowSplitPackagesAction: boolean;
  onSplitPackage: Function;
  settings: any;

  public packages: any[];
  public isTableExpanded: boolean = undefined;
  public totals: any = {
    count: 0,
    requiredSpots: 0,
    pallets: 0,
    weight: 0,
    executedCount: 0,
    executedWeight: 0
  }
  public executedFulfillmentStepsItemsMap = {};
  public fulfillmentStepStatusType: typeof FulfillmentStepStatusType = FulfillmentStepStatusType;
  public fulfillmentPackageSplitType: typeof FulfillmentPackageSplitType = FulfillmentPackageSplitType;
  public sitePackageItems: any[];
  public isWeightInTotal: boolean;
  public columns: any;

  private isViewSettingsChanged = false;

  constructor(private splitService: SplitService, private packageDetailsViewSettingsService: PackageDetailsViewSettingsService) {
  }

  $onInit() {
    this.sitePackageItems = this.splitService.getSitePackageDetailsSettings();
    this.isWeightInTotal = this.splitService.getOmsPackageTotalWeightIndication();
    this.packages = [];
    this._initPackagesTable();
    this.isTableExpanded = this.packages.length < 4;
    this._initTotals();

    // Initialize settings if not provided
    if (!this.settings) {
      this.settings = {
        columns: this._getDefaultColumns(),
        packageDynamicColumns: []
      };
    } else {
      // Filter out columns that this component doesn't use and ensure proper type column setup
      this.settings.columns = this._filterRelevantColumns(this.settings.columns);
      // Ensure packageDynamicColumns is always an array
      this.settings.packageDynamicColumns = this.settings.packageDynamicColumns || [];
    }

    this.columns = this.settings.columns;

    if (this.packageDynamicColumns && Array.isArray(this.packageDynamicColumns)) {
      this.settings.packageDynamicColumns = this.packageDynamicColumns;
    } else {
      this.packageDynamicColumns = this.settings.packageDynamicColumns;
    }

    this.packageDetailsViewSettingsService.load()
      .then((columnsMap) => {
        if (this.isViewSettingsChanged) {
          return;
        }
        const viewColumns = this.packageDetailsViewSettingsService.applyColumnsMap(columnsMap, this.columns, this.packageDynamicColumns);
        this.settings = {...this.settings, ...viewColumns};
        this.columns = viewColumns.columns;
        this.packageDynamicColumns = viewColumns.packageDynamicColumns;
      });

  }

  onToggleTable() {
    this.isTableExpanded = !this.isTableExpanded;
  }



  onViewSettingsChange(settings) {
    this.isViewSettingsChanged = true;
    this.settings = settings || {
      columns: this._getDefaultColumns(),
      packageDynamicColumns: []
    };

    // Filter out columns that this component doesn't use and ensure proper type column setup
    this.settings.columns = this._filterRelevantColumns(this.settings.columns);

    // Reset emits no packageDynamicColumns - fall back to the site default (all hidden).
    this.settings.packageDynamicColumns = this.settings.packageDynamicColumns ?? _.map(this.packageDynamicColumns, c => ({...c, enabled: false}));

    this.columns = this.settings.columns;
    this.packageDynamicColumns = this.settings.packageDynamicColumns;
    this.packageDetailsViewSettingsService.save(this.packageDetailsViewSettingsService.toColumnsMap(this.columns, this.packageDynamicColumns));
  }

  public _getDefaultColumns() {
    return {
      type: { enabled: true, hide: true },
      planned: { enabled: true },
      gap: { enabled: true },
      dimensions: { enabled: true },
      volume: { enabled: true },
      description: { enabled: true },
      labels: { enabled: true },
      legs: { enabled: true },
      barcode: { enabled: true },
      pallets: { enabled: true },
      sku: { enabled: true },
      packsInItem: { enabled: false }
    };
  }

  private _filterRelevantColumns(columns: any): any {
    const defaultColumns = this._getDefaultColumns(); // Cache the defaults
    const relevantColumnKeys = Object.keys(defaultColumns);
    const filtered: any = {};

    // Only include columns that this component actually uses
    relevantColumnKeys.forEach(key => {
      if (columns && columns[key]) {
        filtered[key] = columns[key];
      } else {
        // If column doesn't exist in input, use default
        filtered[key] = defaultColumns[key];
      }
    });

    // Ensure type column is always hidden from settings and enabled
    if (filtered.type) {
      filtered.type.hide = true;
      filtered.type.enabled = true;
    }

    return filtered;
  }


  private _initTotals() {
    _.forEach(this.packagesList, item => {
      this.totals.count += item.count;
      this.totals.requiredSpots += item.requiredSpots * item.count || 0;
      this.totals.pallets += item.pallets || 0;
      this.totals.weight += this.isWeightInTotal ? item.weight: (item.weight * item.count);
    })

    this.fulfillmentLineSteps.forEach(s => {
      if (s.status === this.fulfillmentStepStatusType.Completed) {
        s.items.forEach(item => {

          if (!this.executedFulfillmentStepsItemsMap[item.id]) {
            this.executedFulfillmentStepsItemsMap[item.id] = {
              count: 0,
              weight: 0
            }
          }
          const packageItem = this.packagesList.find(packageItem => packageItem.id === item.id);

          const sitePackageItem = _.find(this.sitePackageItems, sitePackageItem => {
            return sitePackageItem.id === packageItem.type;
          });

          if (sitePackageItem?.splitSettings?.splitType === this.fulfillmentPackageSplitType.BY_WEIGHT) {
            if (item.actualWeight >= 0) {
              this.executedFulfillmentStepsItemsMap[item.id].weight += this.isWeightInTotal ? item.actualWeight : (item.actualWeight * item.actualCount);
            } else {
              this.executedFulfillmentStepsItemsMap[item.id].weight += this.isWeightInTotal ? item.actualWeight : (item.weight * item.count);
            }

            if (this.executedFulfillmentStepsItemsMap[item.id].count === 0) {
              this.executedFulfillmentStepsItemsMap[item.id].count = packageItem.count;
            }
          } else {
            if (item.actualCount >= 0) {
              this.executedFulfillmentStepsItemsMap[item.id].count += item.actualCount;
              this.executedFulfillmentStepsItemsMap[item.id].weight += this.isWeightInTotal ? item.actualWeight : (item.actualWeight * item.actualCount);
            } else {
              this.executedFulfillmentStepsItemsMap[item.id].count += item.count;
              this.executedFulfillmentStepsItemsMap[item.id].weight += this.isWeightInTotal ? item.actualWeight : (item.weight * item.count);
            }
          }

        })
      }
    })

    Object.keys(this.executedFulfillmentStepsItemsMap).forEach((key: string) => {
      this.totals.executedCount += this.executedFulfillmentStepsItemsMap[key].count;
      this.totals.executedWeight += this.executedFulfillmentStepsItemsMap[key].weight;
    })
  }

  private _initPackagesTable() {
    _.forEach(this.packagesList, p => {
      const packageExecutionSteps = _.filter(this.fulfillmentLineSteps, step => {
        return _.includes(_.map(step.items, 'id'), p.id);
      });

      const isTheSameGroupId = _.every(packageExecutionSteps, el => {
        return el.groupId === packageExecutionSteps[0].groupId;
      });

      if (isTheSameGroupId) {
        _.forEach(packageExecutionSteps, el => {
          let packageCopy = angular.copy(p);
          const packageDataInExecutingStep = _.find(el.items, item => {
            return item.id === packageCopy.id;
          });
          packageCopy.count = packageDataInExecutingStep.count;
          packageCopy.weight = packageDataInExecutingStep.weight;
          packageCopy.actualWeight = packageDataInExecutingStep.actualWeight;
          packageCopy.actualCount = packageDataInExecutingStep.actualCount;
          const originalPackageItem = packageCopy.items.find((i)=> i.id === packageDataInExecutingStep.id);
          packageCopy.packsInItem = originalPackageItem?.packsInItem;
          packageCopy.configuredFields = originalPackageItem?.configuredFields;

          const executionStep = _.find(this.fulfillmentLineSteps, step => {
            return step.id === el.id
          });

          // if (!_.isUndefined(packageDataInExecutingStep.actualCount) && (packageDataInExecutingStep.count - packageDataInExecutingStep.actualCount !== 0)) {
          //   packageCopy.hasMissingPackages = true;
          // }

          packageCopy.executionSteps = [];
          packageCopy.executionSteps.push(executionStep);

          this.packages.push(packageCopy);
        })
      } else {
        let packageCopy = angular.copy(p);
        packageCopy.executionSteps = [];
        _.forEach(packageExecutionSteps, el => {
          const packageDataInExecutingStep = _.find(el.items, item => {
            return item.id === packageCopy.id;
          });
          packageCopy.weight = packageDataInExecutingStep.weight;
          packageCopy.actualWeight = packageDataInExecutingStep.actualWeight;
          packageCopy.actualCount = packageDataInExecutingStep.actualCount;
          const originalPackageItem = packageCopy.items.find((i)=> i.id === packageDataInExecutingStep.id);
          packageCopy.packsInItem = originalPackageItem?.packsInItem;
          packageCopy.configuredFields = originalPackageItem?.configuredFields;

          const executionStep = _.find(this.fulfillmentLineSteps, step => {
            return step.id === el.id
          });

          // if (packageCopy.isCountUpdated && !_.isUndefined(packageDataInExecutingStep.actualCount) && (packageDataInExecutingStep.count - packageDataInExecutingStep.actualCount !== 0)) {
          //   packageCopy.hasMissingPackages = true;
          // }

          packageCopy.executionSteps.push(executionStep);
        })
        this.packages.push(packageCopy);
      }
    })
  }
}

angular
  .module('bringoz.admin.fulfillment')
  .component('fulfillmentLinePackagesTable', {
    bindings: {
      packagesList: '<',
      fulfillmentLineSteps: '<',
      weightUnit: '<',
      volumeUnit: '<',
      distanceUnit: '<',
      packageDynamicColumns: '<?',
      isShowSplitPackagesAction: '<',
      onSplitPackage: '&',
      settings: '<?'
    },
    template: require('./fulfillment-line-packages-table.html').default,
    controller: FulfillmentLinePackagesTableComponent
  });
